<template>
  <transition name="gallery-mask">
    <div
      tabindex="1"
      ref="keydownMask"
    >
      <div
        class="gallery-wrapper"
        v-if="showGallery"
        @mousedown.prevent
        @click="closeGallery"
      >
        <span
          class="close-button"
          @click.stop="closeGallery"
        >
          <a-icon type="close" />
        </span>

        <span
          class="navigation-btn next"
          @click.stop="next"
          v-if="currentImage"
          @mousedown.prevent
        >
          <a-icon type="right" />
        </span>

        <span
          class="navigation-btn prev"
          @click.stop="prev"
          v-if="currentImage && previewList.length"
          @mousedown.prevent
        >
          <a-icon type="left" />
        </span>

        <span
          class="status-bar"
          v-if="currentImage"
          @click.stop
        >
          {{ currentIndex + 1 }} из {{ fullCount }}
        </span>

        <div class="gallery-relative">
          <span
            class="preview-navigation icon-arrow_back prev"
            @click.stop="prevPage"
            v-if="previewList.length > 5"
          >
            <a-icon type="left" />
          </span>

          <span
            class="preview-navigation icon-arrow_forward next"
            @click.stop="nextPage"
            v-if="previewList.length > 5"
          >
            <a-icon type="right" />
          </span>

          <div class="cover-wrapper">
            <div
              class="cover"
              @click.stop
              v-if="currentImage"
            >
              <img
                :src="imageUrlWrapper(currentImage)"
                alt=""
              />
            </div>

            <div
              class="error"
              v-else
            >
              {{ $t("common.no_images") }}
            </div>
          </div>

          <div
            class="previews-wrapper"
            v-if="currentImage"
          >
            <div class="box">
              <ul
                class="list"
                :style="{
                  left: `-${offset}px`
                }"
              >
                <li
                  v-for="(image, index) in previewList"
                  :key="`${image.url}-${index}`"
                >
                  <span
                    @click.stop="setCurrentIndex(image.index)"
                    class="preview"
                    :class="{
                      active: image.index == currentIndex
                    }"
                  >
                    <img
                      :src="imageUrlWrapper(image.url)"
                      alt=""
                    />
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { parseUrl } from "@/utils/cdn"
import imageUrlWrapper from "@/utils/imageUrlWrapper"

const cdnUrl = new URL(process.env.VUE_APP_CDN_URL).origin

interface PreviewsItem {
  url: string
  index: number
}
type EventImagesArray = string[]
interface EventImagesObject {
  images: EventImagesArray
  startIndex?: number
}
type EventPayload = EventImagesArray | EventImagesObject

export default {
  name: "TheGallery",
  data() {
    return {
      previewWidth: 84,
      perPage: 5,
      showGallery: false,
      images: [],
      previews: [],
      currentIndex: null,
      offset: 0,
      currentPage: 0,
      activeElement: null,
      cdnUrl
    }
  },
  computed: {
    currentImage(): string {
      if (this.currentIndex != null && this.images.length) {
        return this.images[this.currentIndex]
      } else {
        return ""
      }
    },

    fullCount(): number {
      return this.images.length
    },

    previewList(): PreviewsItem[] {
      return this.previews.map((image, i) => {
        return {
          url: image,
          index: i
        }
      })
    }
  },

  methods: {
    imageUrlWrapper,
    handleEvent(obj: EventPayload): void {
      let images: any[] = []
      let previews: any[] = []
      let currentIndex: number = 0

      if (Array.isArray(obj)) {
        images = obj
      } else if (typeof obj === "object") {
        if ("images" in obj) {
          images = obj.images
        }

        if ("startIndex" in obj) {
          currentIndex = obj.startIndex
        }
      }

      previews = [...images]
      previews = this.getCdnUrls(images, "300x300")
      images = images.filter((str) => typeof str === "string")
      images = this.getCdnUrls(images, "full")

      this.images = images
      this.previews = previews
      this.currentIndex = currentIndex
      this.showGallery = true
      this.setCurrentIndex(this.currentIndex)
      this.setActualOffset()
      this.listenKeyDown()
    },

    getCdnUrls(array: EventImagesArray, size: string): EventImagesArray {
      return array.map((str) => {
        if (~str.indexOf(this.cdnUrl) || str.indexOf("http") == 0) return str
        else return `${this.cdnUrl}/media/assets/images/${parseUrl(str, size)}`
      })
    },

    setActualOffset(): void {
      const steps: number = Math.floor(Number(this.currentIndex) / this.perPage.toFixed(0))
      const stepSize: number = this.perPage * this.previewWidth
      const offset: number = stepSize * steps

      this.currentPage = steps
      this.offset = offset
    },

    listenKeyDown(): void {
      const x = window.scrollX
      const y = window.scrollY

      this.previousActiveElement = document.activeElement
      this.$refs.keydownMask.focus()
      window.scrollTo(x, y)
      this.$refs.keydownMask.addEventListener("keydown", this.keyDownListener)
    },

    removeKeyDownListener(): void {
      this.$refs.keydownMask.removeEventListener("keydown", this.keyDownListener)
      this.previousActiveElement && this.previousActiveElement.focus()
    },

    keyDownListener(e): void {
      switch (e.keyCode) {
        case 37:
          this.prev()
          break

        case 39:
          this.next()
          break

        case 27:
          this.closeGallery()
          break
      }
    },

    closeGallery(): void {
      this.showGallery = false
      this.removeKeyDownListener()
    },

    setCurrentIndex(index): void {
      this.currentIndex = index
      this.setActualOffset()
    },

    next(): void {
      if (this.currentIndex + 1 < this.images.length) {
        this.setCurrentIndex(this.currentIndex + 1)
      } else {
        this.setCurrentIndex(0)
      }
    },

    prev(): void {
      if (this.currentIndex - 1 >= 0) {
        this.setCurrentIndex(this.currentIndex - 1)
      } else {
        this.setCurrentIndex(this.images.length - 1)
      }
    },

    nextPage(): void {
      let nextPage = +this.currentPage + 1

      if ((nextPage + 1) * this.perPage - this.perPage < this.images.length) {
        this.offset = nextPage * this.perPage * this.previewWidth
        this.currentPage = nextPage
      } else {
        this.offset = 0
        this.currentPage = 0
      }
    },

    prevPage(): void {
      let prevPage = +this.currentPage - 1
      let stepSize = this.perPage * this.previewWidth

      if (prevPage >= 0) {
        this.offset -= stepSize
        this.currentPage = prevPage
      } else {
        let pagesCount = Math.ceil(this.images.length / this.perPage) - 1
        this.offset = pagesCount * stepSize
        this.currentPage = pagesCount
      }
    }
  }
}
</script>

<style lang="scss">
.gallery-wrapper {
  z-index: 10000000;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  content: "";
  transition: opacity 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  opacity: 1;
  display: inline-block;
  & .navigation-btn {
    z-index: 99999;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    height: 120px;
    width: 60px;
    line-height: 120px;
    text-align: center;
    box-sizing: border-box;
    position: absolute;
    cursor: pointer;
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &.next {
      top: calc(50% - 60px);
      right: 10px;
    }
    &.prev {
      top: calc(50% - 60px);
      left: 10px;
    }
  }
  & .close-button {
    z-index: 99999;
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    height: 60px;
    width: 60px;
    line-height: 60px;
    text-align: center;
    box-sizing: border-box;
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
  & .status-bar {
    cursor: default;
    z-index: 999;
    position: absolute;
    text-align: center;
    height: 20px;
    line-height: 20px;
    padding: 0 12px;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
  }
  & .gallery-relative {
    position: relative;
    width: 100%;
    height: 100%;
    display: inline-block;
    content: "";
    & .preview-navigation {
      position: absolute;
      height: 60px;
      width: 60px;
      content: "";
      display: inline-block;
      z-index: 9999;
      cursor: pointer;
      text-align: center;
      line-height: 60px;
      font-size: 18px;
      font-weight: 700;
      color: #fff;
      &.next {
        left: calc(50% + 220px);
        bottom: 15px;
      }
      &.prev {
        right: calc(50% + 220px);
        bottom: 15px;
      }
      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
    & .cover-wrapper {
      position: absolute;
      width: calc(100% - 160px);
      height: calc(100% - 100px);
      left: 80px;
      top: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      & .cover {
        display: inline-block;
        max-width: 100%;
        max-height: 100%;
        line-height: 0;
        & img {
          display: inline-block !important;
          max-width: 100%;
          max-height: calc(100vh - 100px);
        }
      }
      & .error {
        display: inline-block;
        max-width: 100%;
        max-height: 100%;
        line-height: 100%;
        text-align: center;
        font-size: 12px;
        color: #fff;
      }
    }
    & .previews-wrapper {
      position: absolute;
      width: calc(100% - 160px);
      padding: 0;
      content: "";
      height: 90px;
      bottom: 0;
      left: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      & .box {
        width: calc(84px * 5);
        height: 80px;
        position: relative;
        overflow: hidden;
        font-size: 0;
        line-height: 0;
      }
      & ul.list {
        position: absolute;
        top: 0;
        transition: left 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
        left: 0;
        list-style: none;
        padding: 0;
        margin: 0;
        width: 10000px;
        height: 80px;
        li {
          padding: 0;
          margin: 0;
          display: inline;
          margin-right: 2px;
          margin-left: 2px;
          & .preview {
            border: 1px solid rgba(0, 0, 0, 0);
            background-color: transparent;
            overflow: hidden;
            width: 80px;
            height: 80px;
            display: inline-block;
            box-sizing: border-box;
            cursor: pointer;
            position: relative;
            transition: opacity 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
            opacity: 0.8;
            &.active {
              border: 1px solid #357cf1;
              opacity: 1;
            }
            &:hover {
              opacity: 1;
            }
            & img {
              max-width: 80px;
              max-height: 80px;
              position: absolute;
              top: calc(50%);
              left: calc(50%);
              transform: translate(-50%, -50%);
            }
          }
        }
      }
    }
  }
}
.gallery-mask-enter {
  opacity: 0;
}
.gallery-mask-leave-active {
  opacity: 0;
}
</style>
